import { Bell, CalendarClock, PiggyBank, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { dateLocale } from '../lib/i18n';
import { trpc } from '../lib/trpc';

const ICONS: Record<string, typeof Bell> = {
  renewal: CalendarClock,
  budget: PiggyBank,
  system: Info,
};

export function NotificationsBell() {
  const { t } = useTranslation();
  const { enabled } = useAuthenticatedQuery();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  // Refresco periódico del contador (cada 60 s)
  const { data } = trpc.notifications.list.useQuery(
    { limit: 15 },
    { enabled, refetchInterval: 60_000 }
  );
  const markRead = trpc.notifications.markRead.useMutation({
    onSuccess: () => utils.notifications.list.invalidate(),
  });
  const markAllRead = trpc.notifications.markAllRead.useMutation({
    onSuccess: () => utils.notifications.list.invalidate(),
  });

  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  const openItem = (n: (typeof items)[number]) => {
    if (!n.readAt) markRead.mutate({ id: n.id });
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-muted-foreground transition hover:border-border hover:bg-accent hover:text-foreground"
        aria-label={t('Notificaciones')}
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white ring-2 ring-background">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 w-80 max-w-[90vw] overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3.5">
              <span className="text-sm font-semibold text-foreground">
                {t('Notificaciones')}
              </span>
              {unread > 0 && (
                <button
                  className="text-xs font-medium text-primary hover:text-primary/80"
                  onClick={() => markAllRead.mutate()}
                >
                  {t('Marcar todas como leídas')}
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {t('No tienes notificaciones.')}
                </p>
              ) : (
                items.map((n: (typeof items)[number]) => {
                  const Icon = ICONS[n.type] ?? Info;
                  return (
                    <button
                      key={n.id}
                      onClick={() => openItem(n)}
                      className={`flex w-full items-start gap-3 border-b border-border/70 px-4 py-3 text-left transition hover:bg-accent ${
                        n.readAt ? 'opacity-60' : ''
                      }`}
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15">
                        <Icon className="h-4 w-4 text-primary" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground">
                          {n.title}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {n.body}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground/75">
                          {new Date(n.createdAt).toLocaleString(dateLocale())}
                        </span>
                      </span>
                      {!n.readAt && (
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-danger" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
